import { Box } from '@material-ui/core';
import { MerchantStructure } from '@nemuru-eng/js-api-client';
import { useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { FormGridContainer } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { AgentsList } from '@/components/widgets/AgentDetailActions/components/CloneAgent/components/Agents/components/AgentsList';
import { NewAgent } from '@/components/widgets/AgentDetailActions/components/CloneAgent/components/Agents/types/NewAgent';
import { SectionTitle } from '@/components/widgets/AgentDetailActions/components/CloneAgent/components/SectionTitle';
import MerchantStructureEntity from '@/entities/MerchantStructureEntity';

interface FormValues {
  agentName: string;
  agentEmail: string;
  channelId: string;
}

interface AgentsFormProps {
  agents: NewAgent[];
  merchantStructure: MerchantStructure;
  addAgent: (agent: NewAgent) => void;
  removeAgent: (agent: NewAgent) => void;
}

export const AgentsForm = ({ agents, merchantStructure, addAgent, removeAgent }: AgentsFormProps) => {
  const reset = useFormContext()?.reset;
  const handleSubmit = useFormContext()?.handleSubmit;

  const onSubmit = (data: FormValues) => {
    if (!data.agentEmail || !data.agentName || !data.channelId) {
      return;
    }

    const channel = MerchantStructureEntity.create({
      merchantStructure,
      channelId: data.channelId,
    }).getChannel();

    if (!channel) {
      console.error(`Channel with id: ${data.channelId} not found`);
      return;
    }

    addAgent({
      agentId: uuidv4(),
      agentName: data.agentName,
      agentEmail: data.agentEmail,
      channelId: data.channelId,
      channelDescription: channel.description,
    });

    reset({ agentName: '', agentEmail: '', channelId: '' });
  };

  const channels = merchantStructure.merchant.channels.map((channel) => ({
    value: channel.id,
    display: channel.description,
  }));

  return (
    <>
      <SectionTitle titleId={'LIST_OF_NEW_AGENTS'} textId={'LIST_OF_NEW_AGENTS_TEXT'} />
      <FormGridContainer fieldVariant={FieldVariant.form} fullWidth>
        <TextFormField name={'agentName'} label={'AGENT_NAME'} />
        <TextFormField
          name={'agentEmail'}
          label={'AGENT_EMAIL'}
          labelIcon={'info'}
          labelIconPosition="end"
          tooltipTitle={<Text tId={'CLONE_AGENT_EMAIL_TOOLTIP'} />}
        />
        <SelectFormField name={'channelId'} label={'CHANNEL'} selectOptions={channels} />
      </FormGridContainer>
      <Box mt={3} />
      <Box display={'flex'} justifyContent={'flex-end'}>
        <StandardButton
          size={'small'}
          variant={'contained'}
          onClick={handleSubmit(onSubmit)}
          startIcon={<i className="material-icons-outlined">add</i>}
        >
          <Text tId={'ADD_AGENT'} />
        </StandardButton>
      </Box>
      <Box mt={3} />
      <AgentsList agents={agents} removeAgent={removeAgent} />
    </>
  );
};
